import { useParams, Link, useLocation } from "react-router-dom";
import { useTrip, useUpdateItinerary } from "@/lib/api/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { ItineraryView } from "@/components/itinerary/ItineraryView";
import { ItineraryMap } from "@/components/itinerary/ItineraryMap";
import { SwapPopover } from "@/components/itinerary/SwapPopover";
import { ShareDialog } from "@/components/share/ShareDialog";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Itinerary, ItineraryItem, Trip } from "@/lib/api/schemas";
import { useIsDesktop } from "@/lib/useIsDesktop";

export default function PlanPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { data: trip, isLoading } = useTrip(id);
  const queryClient = useQueryClient();
  const updateItinerary = useUpdateItinerary(id || "");
  const [localItinerary, setLocalItinerary] = useState<Itinerary | null>(null);
  // On mobile, default to itinerary/map when opening an existing trip.
  // When arriving from HomePage with a fresh prompt, start on chat so the
  // user sees the AI response immediately.
  const hasInitialPrompt = !!(location.state as { initialPrompt?: string } | null)?.initialPrompt;
  const [mobileTab, setMobileTab] = useState<"chat" | "itinerary">(
    hasInitialPrompt ? "chat" : "itinerary"
  );

  // Three layers of state for the timeline<->map link:
  //  - highlightedItemId: transient yellow flash after scroll (existing)
  //  - focusedItemId:     hovered item (ephemeral, updates map focus)
  //  - selectedItemId:    sticky click-selection (persists info window)
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(
    null
  );
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [swapItem, setSwapItem] = useState<ItineraryItem | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [activeDayNumber, setActiveDayNumber] = useState<number | null>(null);
  // Desktop-only: user can collapse the chat pane to hand its space
  // back to the map + timeline. Persisted per-device.
  const [chatCollapsed, setChatCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("plan:chatCollapsed") === "1";
  });
  // Unread-assistant count while the chat pane is collapsed. Resets
  // the moment the pane reopens.
  const [chatUnread, setChatUnread] = useState(0);
  const highlightTimeoutRef = useRef<number | null>(null);
  const isDesktop = useIsDesktop();
  // Brief "Itinerary updated" ribbon shown after an update arrives while
  // the user is on the Chat tab (mobile). Auto-hides after 4s.
  const [updateRibbon, setUpdateRibbon] = useState(false);

  const navState = location.state as { initialPrompt?: string } | null;
  const initialPrompt = navState?.initialPrompt || trip?.title;

  // Map marker clicked -> scroll timeline row into view and flash it.
  const handleMapItemClick = useCallback((itemId: string) => {
    setMobileTab("itinerary");
    setSelectedItemId(itemId);
    requestAnimationFrame(() => {
      const el = document.getElementById(`itinerary-item-${itemId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedItemId(itemId);
        if (highlightTimeoutRef.current) {
          window.clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = window.setTimeout(() => {
          setHighlightedItemId(null);
        }, 2000);
      }
    });
  }, []);

  // Timeline row hovered -> focus the map marker.
  const handleItemFocus = useCallback((itemId: string | null) => {
    setFocusedItemId(itemId);
  }, []);

  // Timeline row clicked -> sticky select + focus.
  const handleItemSelect = useCallback((itemId: string) => {
    setSelectedItemId((prev) => (prev === itemId ? null : itemId));
  }, []);

  // Timeline "↺ Swap" clicked -> open the swap popover.
  const handleItemSwap = useCallback((item: ItineraryItem) => {
    setSwapItem(item);
  }, []);

  // Drag-to-reorder: optimistic local update, persist to server, rollback on failure.
  const handleReorder = useCallback(
    (updated: Itinerary) => {
      if (!id) return;
      const previous = localItinerary || trip?.itinerary || null;
      setLocalItinerary(updated);
      updateItinerary.mutate(updated, {
        onError: () => {
          setLocalItinerary(previous);
        },
      });
    },
    [id, localItinerary, trip?.itinerary, updateItinerary]
  );

  // Apply the chosen swap -> refresh trip from response.
  const handleSwapApplied = useCallback(
    (updated: Trip) => {
      setLocalItinerary(updated.itinerary);
      queryClient.invalidateQueries({ queryKey: ["trips", id] });
    },
    [id, queryClient]
  );

  // Triggered whenever the chat stream pushes an itinerary update. If
  // we're on mobile chat view, flash a "Itinerary updated" ribbon over
  // the map so the user notices the new structured content without us
  // force-switching tabs on them.
  const handleItineraryUpdateReceived = useCallback(() => {
    if (isDesktop) return;
    if (mobileTab !== "chat") return;
    setUpdateRibbon(true);
  }, [isDesktop, mobileTab]);

  useEffect(() => {
    if (!updateRibbon) return;
    const t = window.setTimeout(() => setUpdateRibbon(false), 4000);
    return () => window.clearTimeout(t);
  }, [updateRibbon]);

  const rightPaneRef = useRef<HTMLDivElement>(null);
  const handleSetActiveDay = useCallback(
    (day: number | null) => setActiveDayNumber(day),
    []
  );

  // On mobile, start focused on the first day. The small-viewport map
  // can't comfortably show every day's markers at once — overview
  // becomes a colored dot cloud. Desktop keeps the overview default.
  const defaultedRef = useRef(false);
  useEffect(() => {
    if (defaultedRef.current) return;
    if (isDesktop) return;
    const firstDay = trip?.itinerary?.days?.[0]?.dayNumber;
    if (firstDay && activeDayNumber == null) {
      setActiveDayNumber(firstDay);
      defaultedRef.current = true;
    }
  }, [isDesktop, trip?.itinerary?.days, activeDayNumber]);

  const toggleChatCollapsed = useCallback(() => {
    setChatCollapsed((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(
          "plan:chatCollapsed",
          next ? "1" : "0"
        );
      } catch {
        // ignore storage failures (private mode, etc.)
      }
      return next;
    });
  }, []);

  // Cmd/Ctrl+K toggles the chat pane on desktop. Ignored when the
  // user is typing into an input — we don't want to hijack shortcuts
  // inside the textarea.
  useEffect(() => {
    if (!isDesktop) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "k") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      e.preventDefault();
      toggleChatCollapsed();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop, toggleChatCollapsed]);

  // Clear the unread counter when the chat pane becomes visible.
  useEffect(() => {
    if (!chatCollapsed) setChatUnread(0);
  }, [chatCollapsed]);

  // Track assistant replies arriving while chat is collapsed so we can
  // surface a badge on the rail.
  const handleAssistantMessage = useCallback(() => {
    if (isDesktop && chatCollapsed) {
      setChatUnread((n) => n + 1);
    }
  }, [isDesktop, chatCollapsed]);

  const handleViewItinerary = useCallback(() => {
    setMobileTab("itinerary");
    setUpdateRibbon(false);
    // On desktop the right pane is always visible — scroll it to top
    // so the map + Day 1 are immediately in focus. Works on mobile too
    // once the tab switches in on the next frame.
    requestAnimationFrame(() => {
      rightPaneRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      // Flash the first itinerary item as a spatial cue.
      const firstItem = rightPaneRef.current?.querySelector<HTMLElement>(
        '[id^="itinerary-item-"]'
      );
      if (firstItem) {
        const id = firstItem.id.replace(/^itinerary-item-/, "");
        setHighlightedItemId(id);
        if (highlightTimeoutRef.current) {
          window.clearTimeout(highlightTimeoutRef.current);
        }
        highlightTimeoutRef.current = window.setTimeout(
          () => setHighlightedItemId(null),
          2000
        );
      }
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-ink-light">
        Loading trip...
      </div>
    );
  }

  if (!trip || !id) {
    return (
      <div className="flex h-screen items-center justify-center text-red-300">
        Trip not found
      </div>
    );
  }

  const itinerary = localItinerary || trip.itinerary;
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const handleItineraryUpdate = (updated: Itinerary) => {
    setLocalItinerary(updated);
    queryClient.invalidateQueries({ queryKey: ["trips", id] });
  };

  return (
    <div className="flex h-screen flex-col px-3 py-3 md:px-4 md:py-4">
      <div className="frost-panel mb-3 flex shrink-0 items-center gap-2 rounded-2xl px-3 py-2 md:px-4 md:py-2.5">
        <Link
          to="/trips"
          aria-label="Back to trips"
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-light hover:text-ink hover:bg-white/10"
        >
          <span aria-hidden="true" className="text-base">←</span>
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
          {trip.title || trip.destination || "New Trip"}
        </h1>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          aria-label="Share this trip"
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-light hover:text-ink hover:bg-white/10"
          title="Share"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-4 h-4"
            aria-hidden="true"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </button>
        <div className="shrink-0 flex items-center gap-1 rounded-lg bg-white/10 p-0.5 md:hidden">
          <button
            onClick={() => setMobileTab("chat")}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              mobileTab === "chat"
                ? "bg-accent text-deep"
                : "text-ink-light hover:text-ink"
            }`}
          >
            Chat
          </button>
          <button
            onClick={handleViewItinerary}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
              mobileTab === "itinerary"
                ? "bg-accent text-deep"
                : "text-ink-light hover:text-ink"
            }`}
          >
            Itinerary
          </button>
        </div>
      </div>

      {trip.parent && (
        <div className="mb-3 shrink-0 flex items-center gap-2 text-xs text-ink-light px-1">
          <span aria-hidden="true" className="text-sm">↪</span>
          {trip.parent.shareToken ? (
            <span>
              Based on a trip by{" "}
              <Link
                to={`/shared/${trip.parent.shareToken}`}
                className="text-accent-light hover:text-accent underline"
              >
                {trip.parent.authorName || "another traveller"}
              </Link>
            </span>
          ) : (
            <span title="Original trip no longer available">
              Based on a trip by{" "}
              <span className="text-ink">
                {trip.parent.authorName || "another traveller"}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Mobile: persistent map at top, always visible regardless of tab.
          Gives spatial context even while the user is chatting. */}
      {!isDesktop && (
        <div className="relative mb-3 shrink-0 overflow-hidden rounded-2xl frost-panel p-2">
          <ItineraryMap
            itinerary={itinerary}
            googleMapsApiKey={googleMapsApiKey}
            onItemClick={handleMapItemClick}
            focusedItemId={focusedItemId}
            selectedItemId={selectedItemId}
            activeDayNumber={activeDayNumber}
            onSetActiveDay={handleSetActiveDay}
            heightClass="h-[32vh]"
          />
          {updateRibbon && (
            <button
              type="button"
              onClick={handleViewItinerary}
              className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-accent text-deep text-xs font-semibold shadow-lg shadow-accent/40 animate-pulse"
            >
              🗺 Itinerary updated — tap to view
            </button>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1 gap-3">
        {/* Desktop: chat pane (expanded) — hidden entirely when user
            has collapsed it. Mobile: shows/hides via the tab toggle. */}
        {!(isDesktop && chatCollapsed) && (
          <div
            className={`min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl frost-panel ${
              !isDesktop && mobileTab === "chat"
                ? "flex flex-1"
                : !isDesktop
                  ? "hidden"
                  : ""
            } md:flex md:w-[40%] md:flex-initial relative`}
          >
            {isDesktop && (
              <button
                type="button"
                onClick={toggleChatCollapsed}
                aria-label="Collapse chat (Cmd+K)"
                title="Collapse chat (⌘K)"
                className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-deep/60 border border-white/20 text-ink-light hover:text-ink hover:bg-deep/80 flex items-center justify-center text-sm"
              >
                ‹‹
              </button>
            )}
            <ChatPanel
              tripId={id}
              onItineraryUpdate={handleItineraryUpdate}
              onItineraryUpdateReceived={handleItineraryUpdateReceived}
              onRequestViewItinerary={handleViewItinerary}
              onAssistantMessage={handleAssistantMessage}
              initialPrompt={initialPrompt}
            />
          </div>
        )}

        {/* Desktop: collapsed-chat rail. A narrow strip the user can
            click anywhere to expand. Shows an unread badge when
            assistant messages arrived while collapsed. */}
        {isDesktop && chatCollapsed && (
          <button
            type="button"
            onClick={toggleChatCollapsed}
            aria-label="Expand chat (Cmd+K)"
            title="Expand chat (⌘K)"
            className="shrink-0 w-10 flex flex-col items-center justify-center gap-2 rounded-2xl frost-panel text-ink-light hover:text-ink hover:bg-white/[0.07] transition-colors"
          >
            <span aria-hidden="true" className="text-base">››</span>
            <span className="[writing-mode:vertical-rl] rotate-180 text-xs font-semibold tracking-wider uppercase">
              Chat
            </span>
            {chatUnread > 0 && (
              <span
                aria-label={`${chatUnread} new message${chatUnread === 1 ? "" : "s"}`}
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent text-deep text-[10px] font-bold"
              >
                {chatUnread > 9 ? "9+" : chatUnread}
              </span>
            )}
          </button>
        )}

        <div
          ref={rightPaneRef}
          className={`min-h-0 overflow-y-auto rounded-2xl frost-panel ${
            !isDesktop && mobileTab === "itinerary"
              ? "flex-1"
              : !isDesktop
                ? "hidden"
                : ""
          } md:block ${
            isDesktop && chatCollapsed ? "md:flex-1" : "md:w-[60%]"
          }`}
        >
          <div className="space-y-4 p-4">
            {/* Desktop only — mobile has the map docked at top. */}
            {isDesktop && (
              <ItineraryMap
                itinerary={itinerary}
                googleMapsApiKey={googleMapsApiKey}
                onItemClick={handleMapItemClick}
                focusedItemId={focusedItemId}
                selectedItemId={selectedItemId}
                activeDayNumber={activeDayNumber}
                onSetActiveDay={handleSetActiveDay}
              />
            )}
            <ItineraryView
              itinerary={itinerary}
              highlightedItemId={
                highlightedItemId || selectedItemId
              }
              activeDayNumber={activeDayNumber}
              onSetActiveDay={handleSetActiveDay}
              onItemFocus={handleItemFocus}
              onItemSelect={handleItemSelect}
              onItemSwap={handleItemSwap}
              onReorder={handleReorder}
            />
          </div>
        </div>
      </div>

      {swapItem && id && (
        <SwapPopover
          tripId={id}
          item={swapItem}
          onClose={() => setSwapItem(null)}
          onApplied={handleSwapApplied}
        />
      )}

      {shareOpen && (
        <ShareDialog trip={trip} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
}
